if (window.CarouselBase && !customElements.get('custom-gallery')) {
  // Centered peek carousel with an infinite loop. A clone of the last slide is
  // prepended and a clone of the first appended so the rest position always
  // shows previous + active + next; on reaching a clone we jump to the real
  // slide without animation.
  class CustomGallery extends window.CarouselBase {
    connectedCallback() {
      this.viewport = this.querySelector('.gallery__viewport');
      this.track = this.querySelector('.gallery__track');
      this.dots = Array.from(this.querySelectorAll('.gallery__dot'));
      this.originals = Array.from(this.track.children);
      this.count = this.originals.length;
      if (this.count === 0) return;

      this.index = 0;
      this.offset = 0;

      if (this.count > 1) {
        this.addClones();
        this.bindControls();
      }

      this.setActive(0);
      requestAnimationFrame(() => this.go(0, false));
    }

    // Two clones per side so the slide we land on at a wrap always has a real
    // neighbour filling each peek — avoids the empty-peek flash on loop.
    // Layout: [s(n-2) s(n-1)] [s0 ... s(n-1)] [s0 s1]
    addClones() {
      const n = this.count;
      const clone = (slide) => {
        const copy = slide.cloneNode(true);
        copy.classList.add('gallery__slide--clone');
        copy.setAttribute('aria-hidden', 'true');
        const img = copy.querySelector('img');
        if (img) img.loading = 'eager';
        return copy;
      };

      const first = this.originals[0];
      this.track.insertBefore(clone(this.originals[(n - 2 + n) % n]), first);
      this.track.insertBefore(clone(this.originals[n - 1]), first);
      this.track.appendChild(clone(this.originals[0]));
      this.track.appendChild(clone(this.originals[1 % n]));

      this.slides = Array.from(this.track.children);
      this.offset = 2;
    }

    bindControls() {
      this.querySelector('.gallery__arrow--next').addEventListener('click', () => this.step(1));
      this.querySelector('.gallery__arrow--prev').addEventListener('click', () => this.step(-1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

      this.onEnd = (event) => {
        if (event.propertyName !== 'transform') return;
        this.animating = false;
        if (this.wrapTo != null) {
          this.translate(this.wrapTo, false);
          this.wrapTo = null;
        }
      };
      this.track.addEventListener('transitionend', this.onEnd);

      this.onResize = () => this.translate(this.index + this.offset, false);
      window.addEventListener('resize', this.onResize);
    }

    teardown() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    gap() {
      const styles = getComputedStyle(this.track);
      return parseFloat(styles.columnGap || styles.gap) || 0;
    }

    translate(domIndex, animate = true) {
      const slideWidth = (this.slides || this.originals)[0].offsetWidth;
      const x = this.viewport.clientWidth / 2 - slideWidth / 2 - domIndex * (slideWidth + this.gap());
      this.animating = animate && !this.reducedMotion;
      this.track.style.transition = this.animating
        ? 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';
      this.track.style.transform = `translateX(${x}px)`;
    }

    // Jump straight to a real slide (dots).
    go(index, animate = true) {
      if (this.animating) return;
      this.index = (index + this.count) % this.count;
      this.wrapTo = null;
      this.translate(this.index + this.offset, animate);
      this.setActive(this.index);
    }

    // Move one slide, looping through the clones at the edges.
    step(direction) {
      if (this.animating) return;
      const target = this.index + direction;
      this.index = (target + this.count) % this.count;

      if (target < 0) {
        this.translate(1, true);
        this.wrapTo = this.count + 1;
      } else if (target >= this.count) {
        this.translate(this.count + 2, true);
        this.wrapTo = this.offset;
      } else {
        this.translate(this.index + this.offset, true);
        this.wrapTo = null;
      }
      this.setActive(this.index);
    }

    setActive(index) {
      this.originals.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  customElements.define('custom-gallery', CustomGallery);
}
